export interface JobPosting {
  id: string;
  title: string;
  employer: string;
  location: string;
  postedDate: string;
}

export interface JobApplication {
  id: string;
  jobId: string;
  applicantSub: string;
  status: 'submitted';
  submittedAt: string;
  // English, unchanged -- this is the published v1 shape
  // (check-api-versioning only checks route registration, not response
  // shape, so changing this field itself would be a silent breaking change
  // to GET /api/v1/applications with no CI backstop).
  jobTitle: string | null;
  // Additive bilingual sibling (citizen_context/CLAUDE.md's "new fields are
  // optional; consumers must tolerate unknown fields; never break an
  // existing reader"). PLATFORM_STANDARDS requires EN and FR everywhere a
  // citizen sees it -- JobApplicationsList reads this by its own locale,
  // falling back to jobTitle (English) when a BFF hasn't rolled this field
  // out yet.
  jobTitleBilingual?: BilingualText;
  employer: string | null;
}

export interface BilingualText {
  en: string;
  fr: string;
}

/** A single employer-authored skill requirement on a posting. */
export interface PostingSkill {
  id: string;
  en: string;
  fr: string;
}

/** One citizen skill that matched a posting skill row. */
export interface SkillMatch {
  skillId: string;    // PostingSkill.id
  citizenTerm: string; // verbatim, untranslated
}

/** Richer shape returned by the recommendations endpoint. */
export interface JobListing {
  id: string;
  title: BilingualText;
  employer: string;
  province: string;
  city: string;
  salaryMin: number;
  salaryMax: number;
  workArrangement: string;
  postedDaysAgo: number;
  matchReasons: BilingualText[];
  description: BilingualText;
  responsibilities: BilingualText[];
  skillsChecklist: PostingSkill[];
  // Lowercase free-text terms matched against a citizen's skills. Internal
  // sorting input, never displayed in the UI.
  searchableSkills: string[];
  // Employer-authored perks shown as pills in the Details section. Every
  // entry must be unambiguously a benefit (not a requirement or role context).
  benefits: BilingualText[];
  // ISO-8601 date string (YYYY-MM-DD) when the role starts, or null when TBD.
  startDate: string | null;
}

export interface Skill {
  id: string;
  label: BilingualText;
}

export type SortBy = 'best-skills-fit' | 'most-recent' | 'highest-pay';

export interface JobRecommendationsResponse {
  listings: JobListing[]; // current page, or the limit slice
  total: number; // full pool count before pagination
  page?: number;
  pageSize?: number;
  province?: string;
  address?: string;
  // Per-citizen matches keyed by listing id, over the whole pool; listings with no match have no key.
  matchedSkills: Record<string, SkillMatch[]>;
}

export interface SkillsResponse {
  skills: string[]; // current skill list for the authenticated citizen
}

/** Wire shape for POST /api/v1/documents/:type -- never echoes file content back. */
export interface DocumentUploadResponse {
  type: string;
  filename: string;
  size: number;
  uploadedAt: string; // ISO-8601
}
